import { TransactionOverrides } from '@arcxgame/contracts/dist/arc-types/ethereum'
import { IERC20Factory } from '@arcxgame/contracts/dist/src/typings/IERC20Factory'
import { BigNumber, constants, Contract, Signer, utils } from 'ethers'
import { approve } from 'src/utils/approve'
import { eqIgnoreCase } from '@arcxmoney/arcx-core'

export interface LiquidityRequest {
  erc20Address: string
  amount: BigNumber
}

export interface PoolInfo {
  threePoolDeposited: BigNumber
  tokenDeposited: BigNumber
  totalLiquidity: BigNumber
}

export interface CurvePoolConfig {
  minimalPoolPartPercentage: string
  poolRegistryAddress: string
  poolAddress: string
  depositZap: string
}

export class CurveService {
  private poolRegistry: Contract
  private depositZapContract: Contract
  lpToken: Contract
  private poolContract: Contract

  constructor(public signer: Signer, public config: CurvePoolConfig) {
    this.poolRegistry = this.getPoolRegistryContract(config.poolRegistryAddress)
    this.depositZapContract = new Contract(
      config.depositZap,
      [
        'function calc_token_amount(address, uint256[4], bool) view returns (uint256)',
        'function calc_withdraw_one_coin(address, uint256, int128) view returns (uint256)',
        'function add_liquidity(address, uint256[4], uint256) returns (uint256)',
        'function remove_liquidity(address, uint256, uint256[4]) returns (uint256)',
        'function remove_liquidity_one_coin(address, uint256, int128, uint256) returns (uint256)',
        'function remove_liquidity_imbalance(address _pool, uint256[4] _amounts, uint256 _max_burn_amount) nonpayable returns (uint256)',
      ],
      signer,
    )
    this.lpToken = IERC20Factory.connect(config.poolAddress, signer)
    this.poolContract = this.getPoolContract(config.poolAddress)
  }

  getPoolRegistryContract(address: string) {
    return new Contract(
      address,
      ['function get_underlying_coins(address) view returns (address[8])'],
      this.signer,
    )
  }

  getPoolContract(poolAddress: string) {
    return new Contract(
      poolAddress,
      [
        'function get_virtual_price() view returns (uint256)',
        'function balances(uint256) view returns (uint256)',
        'function get_dy_underlying(int128 i, int128 j, uint256 dx) view returns (uint256)',
        'function exchange_underlying(int128 i, int128 j, uint256 dx, uint256 min_dy) nonpayable returns(uint256)',
      ],
      this.signer,
    )
  }

  async getPoolInfo(): Promise<PoolInfo> {
    const threePoolDeposited = await this.poolContract.balances(1)
    const tokenDeposited = await this.poolContract.balances(0)
    return {
      threePoolDeposited,
      tokenDeposited,
      totalLiquidity: tokenDeposited.add(threePoolDeposited),
    }
  }

  async getLpBalance() {
    return this.lpToken.balanceOf(await this.signer.getAddress())
  }

  async withdrawSingle(
    targetErc20Address: string,
    amount: BigNumber,
    expectedAmount: BigNumber,
    overrides: TransactionOverrides = {},
  ) {
    const indexOfTargetToken = await this.getIndexOfBaseCoin(targetErc20Address)
    await approve(amount, this.lpToken.address, this.depositZapContract.address, this.signer)
    const expectedWithThreshold = expectedAmount.mul(98).div(100)
    return this.depositZapContract.remove_liquidity_one_coin(
      this.poolContract.address,
      amount,
      indexOfTargetToken,
      expectedWithThreshold,
      overrides,
    )
  }

  async calcExpectedSingleWithdrawAmount(targetErc20Address: string, amount: BigNumber) {
    if (amount.isZero()) {
      return constants.Zero
    }
    const indexOfTargetToken = await this.getIndexOfBaseCoin(targetErc20Address)
    return this.depositZapContract.calc_withdraw_one_coin(
      this.poolContract.address,
      amount,
      indexOfTargetToken,
    )
  }

  async deposit(
    depositRequests: LiquidityRequest[],
    expectedAmount: BigNumber,
    overrides: TransactionOverrides = {},
  ) {
    const depositAmounts = await this.getAmountsArray(depositRequests)
    for (const { erc20Address, amount } of depositRequests) {
      await approve(amount, erc20Address, this.depositZapContract.address, this.signer)
    }
    const expectedWithThreshold = expectedAmount.mul(98).div(100)
    return this.depositZapContract.add_liquidity(
      this.poolContract.address,
      depositAmounts,
      expectedWithThreshold,
      overrides,
    )
  }

  async withdraw(
    withdrawRequests: LiquidityRequest[],
    expectedMaxLpAmount: BigNumber,
    overrides?: TransactionOverrides,
  ) {
    const expectedWithThreshold = expectedMaxLpAmount.mul(102).div(100)
    await approve(
      expectedWithThreshold,
      this.lpToken.address,
      this.depositZapContract.address,
      this.signer,
    )
    const withdrawAmounts = await this.getAmountsArray(withdrawRequests)
    return this.depositZapContract.remove_liquidity_imbalance(
      this.poolContract.address,
      withdrawAmounts,
      expectedWithThreshold,
      overrides,
    )
  }

  async getAmountsArray(liquidityRequests: LiquidityRequest[]) {
    const array = new Array(4).fill(constants.Zero)
    for (const { erc20Address, amount } of liquidityRequests) {
      const indexOfDepositedValue = await this.getIndexOfBaseCoin(erc20Address)
      array[indexOfDepositedValue] = amount
    }
    return array
  }

  async calcExpectedDepositAmount(depositRequests: LiquidityRequest[]) {
    const depositAmounts = await this.getAmountsArray(depositRequests)
    return this.depositZapContract.calc_token_amount(
      this.poolContract.address,
      depositAmounts,
      true,
    )
  }

  async calcExpectedWithdrawAmount(withdrawRequests: LiquidityRequest[]) {
    const withdrawAmounts = await this.getAmountsArray(withdrawRequests)
    return this.depositZapContract.calc_token_amount(
      this.poolContract.address,
      withdrawAmounts,
      false,
    )
  }

  private async getIndexOfBaseCoin(erc20Address: string) {
    const underlyingCoins = await this.poolRegistry.get_underlying_coins(this.poolContract.address)
    const indexOfDepositedValue = underlyingCoins.findIndex((coin) =>
      eqIgnoreCase(coin, erc20Address),
    )
    return indexOfDepositedValue
  }

  async getExpectedSwappedAmount(from: string, to: string, amount: BigNumber) {
    if (from === to) {
      return amount
    }
    const i = await this.getIndexOfBaseCoin(from)
    const y = await this.getIndexOfBaseCoin(to)

    return this.poolContract.get_dy_underlying(i, y, amount)
  }

  async swap(
    from: string,
    to: string,
    amount: BigNumber,
    expectedAmount: BigNumber,
    overrides: TransactionOverrides = {},
  ) {
    const i = await this.getIndexOfBaseCoin(from)
    const y = await this.getIndexOfBaseCoin(to)
    await approve(amount, from, this.poolContract.address, this.signer)
    const expectedWithThreshold = expectedAmount.mul(98).div(100)
    return this.poolContract.exchange_underlying(i, y, amount, expectedWithThreshold, overrides)
  }

  isPoolStable(stableTokenPercentage: BigNumber) {
    const minPercentageBn = utils.parseEther(this.config.minimalPoolPartPercentage)
    const maxPercentageBn = utils.parseEther('100').sub(minPercentageBn)
    return (
      stableTokenPercentage.gte(this.config.minimalPoolPartPercentage) &&
      stableTokenPercentage.lte(maxPercentageBn)
    )
  }
}

export const threePoolAddresses = {
  USDC: '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48',
  DAI: '0x6b175474e89094c44da98b954eedeac495271d0f',
  USDT: '0xdac17f958d2ee523a2206206994597c13d831ec7',
}

export const stableTokenAddresses = {
  STABLEX: '0xcD91538B91B4ba7797D39a2f66E63810b50A33d0',
}

export const curvePoolAddresses = {
  ...threePoolAddresses,
  ...stableTokenAddresses,
}
